"use client";
import {
  MatchScheduleSection as MatchScheduleSectionUI,
  MatchScheduleSectionProps as MatchScheduleSectionUIProps,
  MatchScheduleItemProps,
} from "@repo/ui-parkmobile/sections/MatchScheduleSection";
import {
  sectionHeaderResolver,
  assetResolver,
  buttonResolver,
} from "@/lib/storyblok/adaptors";
import { appendMatchDateToUrl } from "@repo/ui-parkmobile/utils/dateTime";
import { storyblokEditable } from "@storyblok/react/rsc";
import { useMemo } from "react";
import type { Blok, Asset, SectionHeader, Button } from "@/generated/sb";

// Types for Storyblok match schedule schema
// These will be replaced by generated types once the schema is created in Storyblok
type MatchScheduleItemBlok = Blok & {
  component: "matchScheduleItem";
  teamAName?: string;
  teamAFlag?: Asset;
  teamBName?: string;
  teamBFlag?: Asset;
  stage?: string;
  matchDate?: string;
  reservationButton?: Button[];
  appendMatchDate?: boolean;
};

type MatchScheduleSectionBlok = Blok & {
  component: "matchScheduleSection";
  typography?: SectionHeader[];
  matches?: MatchScheduleItemBlok[];
  backgroundColor?: "none" | "light" | "accent" | "accent-light";
  topPadding?: "none" | "default" | "large";
  bottomPadding?: "none" | "default" | "large";
};

export type MatchScheduleSectionProps = {
  blok: MatchScheduleSectionBlok;
};

function isFutureMatch(matchDate?: string): boolean {
  if (!matchDate) return true; // Show matches without dates (TBD)
  const date = new Date(matchDate);
  return date > new Date();
}

function formatMatchDate(matchDate?: string): {
  dayNumber: string;
  monthAbbr: string;
} {
  if (!matchDate) return { dayNumber: "?", monthAbbr: "TBD" };

  const d = new Date(matchDate);
  const dayNumber = String(d.getDate());
  const monthAbbr = d
    .toLocaleDateString("en-US", { month: "short" })
    .toUpperCase();

  return { dayNumber, monthAbbr };
}

export function MatchScheduleSection({ blok }: MatchScheduleSectionProps) {
  const typographyProps = useMemo(
    () => sectionHeaderResolver(blok.typography),
    [blok.typography],
  );

  const resolvedMatches = useMemo(() => {
    if (!blok.matches) return [];

    return blok.matches
      .filter((match) => isFutureMatch(match.matchDate))
      .map((match): MatchScheduleItemProps => {
        const teamAFlag = match.teamAFlag
          ? assetResolver(match.teamAFlag)
          : null;
        const teamBFlag = match.teamBFlag
          ? assetResolver(match.teamBFlag)
          : null;
        const { dayNumber, monthAbbr } = formatMatchDate(match.matchDate);

        const resolvedButton = match.reservationButton?.[0]
          ? buttonResolver(match.reservationButton[0])
          : null;

        if (
          resolvedButton &&
          !resolvedButton.navatticUrl &&
          match.appendMatchDate &&
          match.matchDate
        ) {
          resolvedButton.href = appendMatchDateToUrl(
            resolvedButton.href,
            match.matchDate,
          );
        }

        return {
          _uid: match._uid,
          teamA: {
            name: match.teamAName || "TBD",
            flagSrc: teamAFlag?.src,
          },
          teamB: {
            name: match.teamBName || "TBD",
            flagSrc: teamBFlag?.src,
          },
          stage: match.stage || "",
          dayNumber,
          monthAbbr,
          reservationButton: resolvedButton || undefined,
        };
      });
  }, [blok.matches]);

  const componentProps: MatchScheduleSectionUIProps = {
    typography: typographyProps,
    matches: resolvedMatches,
    topPadding: blok.topPadding,
    bottomPadding: blok.bottomPadding,
    backgroundColor: blok.backgroundColor,
  };

  return (
    <section
      {...storyblokEditable(blok)}
      className="uipm:px-[var(--container-margin)]"
    >
      <MatchScheduleSectionUI {...componentProps} />
    </section>
  );
}
